<template>
  <CSidebar
    aside
    :show="$store.state.assignHeroImageToRestaurantAsideShow"
    @update:show="(val) => $store.commit('set', ['assignHeroImageToRestaurantAsideShow', val])"
    colorScheme="light"
    size="lg"
    :fixed="true"
  >
    <CSidebarClose @click.native="$store.commit('toggle', 'assignHeroImageToRestaurantAsideShow')" />

    <CListGroup class="scrollable list-group-accent mt-5">
      <CListGroupItem class="mb-0 list-group-item-accent-secondary bg-light text-center font-weight-bold text-uppercase">
        Assign to Restaurant
      </CListGroupItem>

      <CListGroupItem class="list-group-item-divider list-group-item-accent-primary">

        <div class="image-container">
          <img :src="image?.image_url" />
        </div>

        <CForm @submit.prevent="add">
          <div class="form-group">
            <label class="d-block">Tag Filter</label>
            <v-select
              class="v-select-style"
              placeholder="Select.."
              v-model="form.tags"
              label="name"
              :reduce="t => t.name"
              :options="image?.tags"
              multiple
              @input="getRestaurantsByTypes"
            />
          </div>

          <div class="form-group">
            <div class="d-flex justify-content-between">
              <label>Selected Restaurants</label>
              <div class="d-flex flex-gap-2">
                <CLink @click="form.restaurants = []"><CIcon name="cil-library" /></CLink>
              </div>
            </div>
            <ul class="checklist">
              <li v-for="item in restaurants.filter(r => form.restaurants.includes(r.id))">
                <label class="for-checkbox d-inline-block">
                  <input
                    type="checkbox"
                    v-model="form.restaurants"
                    :value="item.id"
                  />
                  #{{ item.id }} - {{ item.restaurant_name }}
                </label>
                <span v-if="item.hero_image?.image_url" class="ml-2 text-info h5" v-img="{ src: item.hero_image.image_url, openOn: 'click' }">❀</span>
              </li>
            </ul>
          </div>

          <div class="form-group">
            <div class="d-flex justify-content-between">
              <label>Restaurants</label>
              <div class="d-flex flex-gap-2">
                <CLink @click="form.restaurants = filteredRestaurants.pluck('id')"><CIcon name="cil-library-add" /></CLink>
                <CLink @click="form.restaurants = []"><CIcon name="cil-library" /></CLink>
              </div>
            </div>
            <ul class="checklist">
              <li v-for="item in filteredRestaurants">
                <label class="for-checkbox d-inline-block">
                  <input
                    type="checkbox"
                    v-model="form.restaurants"
                    :value="item.id"
                  />
                  #{{ item.id }} - {{ item.restaurant_name }}
                </label>
                <span v-if="item.hero_image?.image_url" class="ml-2 text-info h5" v-img="{ src: item.hero_image.image_url, openOn: 'click' }">❀</span>
              </li>
            </ul>
          </div>
        </CForm>
      </CListGroupItem>

      <CListGroupItem class="sticky-bottom list-group-item-accent-primary bg-white">
        <CButton
          color="primary"
          :disabled="!form.restaurants?.length"
          @click="assign"
        >
          <CIcon name="cil-send" /> Submit
        </CButton>
      </CListGroupItem>

    </CListGroup>

    <CElementCover :opacity="0.4" v-show="loading || submitted"/>
  </CSidebar>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AssignHeroImageToRestaurant",
  props: {
    image: { default: () => ({}), type: Object },
  },
  data() {
    return {
      form: { },
      restaurants: [],
      filteredRestaurants: [],

      loading: false,
      submitted: false,
      errorAlertMessage: '',
    };
  },

  watch: {
    async reloadParams() {
      this.loading = true;
      if (this.$store.state.assignHeroImageToRestaurantAsideShow) {
        this.form = {
          restaurants: [],
          tags: [],
        };
        this.filteredRestaurants = [];

        await this.getRestaurantsByTypes();
      }
      this.loading = false;
    },
  },

  computed: {
    ...mapGetters(["errors"]),
    reloadParams() {
      return [this.$store.state.assignHeroImageToRestaurantAsideShow];
    },
  },

  methods: {
    async getRestaurantsByTypes() {
      if (!this.form.tags.length && this.restaurants.length) {
        this.filteredRestaurants = this.restaurants;
        return;
      }

      this.loading = true;

      await this.$axios
        .get(this.$backend.RESTAURANT.GET_ALL_BY_TYPES, {
          params: {
            type_names: this.form.tags.toString(),
          },
        })
        .then(({data}) => {
          this.filteredRestaurants = data;

          if (!(this.form.tags.length || this.restaurants.length)) {
            this.restaurants = data;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async assign() {
      try {
        this.submitted = true;

        const {data} = await this.$axios.post(this.$backend.RESTAURANT.ASSIGN_HERO_IMAGE, {
          "image_id": this.image.id,
          "restaurants": this.form.restaurants.toString(),
        });

        this.$store.commit('set', ['assignHeroImageToRestaurantAsideShow', false]);
        this.$toast.success(`${data.message}`);
        this.restaurants = [];

        // Show warnings to operator but continue
        if (data?.warnings) {
          const dups = data.warnings.duplicateHeroInZip?.length || 0;
          if (dups > 0) {
            const restaurants = data.warnings.duplicateHeroInZip.map(zip => zip.restaurants.map(restaurant => restaurant.name).join(', ')).join(', ');
            this.$toast.warning(
              `Warning: The hero image is used by multiple restaurants in the same zip: ${restaurants}`, { timeout: 0 });
          }
        }
      } catch (error) {
        this.$toast.error(typeof this.errors === 'object'
          ? Object.values(this.errors).join()
          : this.errors
        );
      } finally {
        this.submitted = false;
      }
    },
  }
};
</script>

<style scoped>
.scrollable {
  overflow-y: auto;
  height: 100%;
}

.image-container {
  padding: 0.25rem;
  outline: 3px solid #3498db;
  border-radius: 5px;
  margin-bottom: 20px;
}
.image-container img {
  max-width: 100%;
  height: auto;
  border-radius: 5px;
}

ul.checklist {
  list-style: none;
  padding-left: 0.5rem;
}
ul.checklist li {
  line-height: 1.2;
  font-size: 1em;
}
ul.checklist input {
  width: 1rem;
  height: 1rem;
}
label.for-checkbox, label.for-checkbox > input {
  cursor: pointer;
}
label.for-checkbox {
  display: flex;
  /* align-items: center; */
  gap: 0.25rem;
}
</style>
